import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// Helper function to calculate contrast ratio
function getContrastRatio(color1, color2) {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  // Calculate relative luminance
  const getLuminance = (rgb) => {
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
      val = val / 255
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)

  if (!rgb1 || !rgb2) return 0

  const lum1 = getLuminance(rgb1)
  const lum2 = getLuminance(rgb2)

  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)

  return (lighter + 0.05) / (darker + 0.05)
}

function BrandColorsSection({
  textColor,
  backgroundColor,
  useBackground,
  onTextColorChange,
  onBackgroundColorChange,
  onUseBackgroundChange
}) {
  const { t } = useTranslation('settings')
  const [contrastRatio, setContrastRatio] = useState(0)
  const [showContrastWarning, setShowContrastWarning] = useState(false)

  // Calculate contrast ratio when colors change
  useEffect(() => {
    if (useBackground) {
      const ratio = getContrastRatio(textColor, backgroundColor)
      setContrastRatio(ratio)
      // WCAG AA requires minimum 4.5:1 for normal text
      setShowContrastWarning(ratio < 4.5)
    } else {
      setContrastRatio(0)
      setShowContrastWarning(false)
    }
  }, [textColor, backgroundColor, useBackground])

  // Handle hex input change
  const handleHexInputChange = (value, onChange) => {
    // Allow # at the start and 0-6 hex characters
    const cleaned = value.replace(/[^#0-9A-Fa-f]/g, '')
    if (cleaned.length <= 7) {
      onChange(cleaned)
    }
  }

  // Validate and complete hex color
  const handleHexInputBlur = (value, onChange) => {
    // If it's a valid 6-digit hex (with or without #), normalize it
    const match = value.match(/^#?([0-9A-Fa-f]{6})$/)
    if (match) {
      onChange('#' + match[1].toUpperCase())
    } else if (value.length === 0) {
      // Reset to default if empty
      onChange(textColor === value ? '#FFFFFF' : '#000000')
    }
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{t('colors.title')}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t('colors.description')}
        </p>
      </div>

      {/* Color Pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Text Color */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('colors.textColorLabel')}
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={textColor}
              onChange={(e) => onTextColorChange(e.target.value)}
              className="h-12 w-12 rounded-lg cursor-pointer border-2 border-gray-300"
            />
            <input
              type="text"
              value={textColor}
              onChange={(e) => handleHexInputChange(e.target.value, onTextColorChange)}
              onBlur={(e) => handleHexInputBlur(e.target.value, onTextColorChange)}
              placeholder="#FFFFFF"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              maxLength={7}
            />
          </div>
          <p className="text-xs text-gray-500">
            {t('colors.textColorHelp')}
          </p>
        </div>

        {/* Background Color */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('colors.backgroundColorLabel')}
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => onBackgroundColorChange(e.target.value)}
              className="h-12 w-12 rounded-lg cursor-pointer border-2 border-gray-300"
              disabled={!useBackground}
            />
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => handleHexInputChange(e.target.value, onBackgroundColorChange)}
              onBlur={(e) => handleHexInputBlur(e.target.value, onBackgroundColorChange)}
              placeholder="#000000"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!useBackground}
              maxLength={7}
            />
          </div>
          <p className="text-xs text-gray-500">
            {t('colors.backgroundColorHelp')}
          </p>
        </div>
      </div>

      {/* Use Background Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="text-sm font-medium text-gray-900">
            {t('colors.useBackgroundLabel')}
          </label>
          <p className="text-xs text-gray-600 mt-1">
            {t('colors.useBackgroundHelp')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onUseBackgroundChange(!useBackground)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            useBackground ? 'bg-primary-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              useBackground ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Contrast Warning */}
      {useBackground && showContrastWarning && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <span className="text-xl mr-3">⚠️</span>
            <div>
              <h4 className="text-sm font-semibold text-yellow-900">{t('colors.contrastWarningTitle')}</h4>
              <p className="text-sm text-yellow-700 mt-1">
                {t('colors.contrastWarningDescription', { contrastRatio: contrastRatio.toFixed(2) })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contrast Info */}
      {useBackground && !showContrastWarning && contrastRatio > 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <span className="text-xl mr-3">✓</span>
            <div>
              <h4 className="text-sm font-semibold text-green-900">{t('colors.contrastGoodTitle')}</h4>
              <p className="text-sm text-green-700 mt-1">
                {t('colors.contrastGoodDescription', { contrastRatio: contrastRatio.toFixed(2) })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {t('colors.previewLabel')}
        </label>
        <div className="relative w-full h-64 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg overflow-hidden shadow-lg">
          {/* Sample Story Background */}
          <div className="absolute inset-0 flex items-center justify-center p-8">
            {/* Caption Text */}
            <div className="text-center max-w-md">
              {useBackground ? (
                <div
                  style={{ backgroundColor }}
                  className="inline-block px-6 py-3 rounded-lg"
                >
                  <p
                    style={{ color: textColor }}
                    className="text-lg font-semibold leading-relaxed"
                  >
                    {t('colors.previewText')}
                  </p>
                </div>
              ) : (
                <p
                  style={{
                    color: textColor,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                  }}
                  className="text-lg font-semibold leading-relaxed"
                >
                  {t('colors.previewText')}
                </p>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 text-center">
          {t('colors.previewHelp')}
        </p>
      </div>
    </div>
  )
}

export default BrandColorsSection
